import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, HardHat, Globe2, FileCheck2, ShieldAlert, Church, Landmark, BrainCircuit, Fingerprint, Lock, LifeBuoy, Check } from "lucide-react";

const IMG_SECURITY = "https://images.unsplash.com/photo-1566245024852-04fbf7842ce9";
const IMG_OPS = "https://images.unsplash.com/photo-1605810230434-7631ac76ec81";
const IMG_EXPORT = "https://images.unsplash.com/photo-1606185540834-d6e7483ee1a4";
const IMG_DEFENSE = "https://images.unsplash.com/photo-1712179355181-cd9add37f76a";
const IMG_CMMC = "https://images.unsplash.com/photo-1526628953301-3e589a6a8b74";
const IMG_CONTINUITY = "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40";
const IMG_AI = "https://images.unsplash.com/photo-1558494949-ef010cbdcc31";
const IMG_PRIVACY = "https://images.unsplash.com/photo-1633265486064-086b219458ec";
const IMG_FOUNDER = "https://images.unsplash.com/photo-1735494032948-14ef288fc9d3";
const IMG_FACILITY = "https://images.unsplash.com/photo-1542744173-8e7e53415bb0";
const IMG_CHURCH = "https://images.unsplash.com/photo-1473177104440-ffee2f376098";

const sections = [
  {
    n: "01",
    Icon: ShieldCheck,
    title: "Security Plans",
    sub: "Physical, operational, and regulator-ready.",
    body:
      "We deliver site-specific security plans that pass scrutiny from boards, insurers, and federal regulators. Engagements typically include a credible-threat assessment, layered physical-security architecture, access-control design, and an incident-response playbook your team can actually execute under pressure. Where the regime requires it, we author Security Vulnerability Assessments and Site Security Plans under CFATS and API-780.",
    bullets: [
      "Site-specific security plans, threat assessments, and DBT alignment",
      "SVA & SSP development under CFATS (Tier I–IV) and API-780",
      "CCTV, access control, and insider-threat program design",
      "Crisis & incident-response playbooks with tabletop exercises",
    ],
    image: IMG_OPS,
    layout: "image-right",
  },
  {
    n: "02",
    Icon: Globe2,
    title: "Export Compliance",
    sub: "ITAR, EAR, OFAC — built for audit.",
    body:
      "We have stood up — and rescued — export-compliance programs across aerospace, semiconductors, energy services, and dual-use manufacturing. Whether you are filing your first DSP-5 or untangling a voluntary disclosure, the work is methodical, documented, and defensible.",
    bullets: [
      "ITAR / EAR jurisdiction & classification (CJ, CCATS)",
      "License determination, application, and management",
      "Restricted-party and end-use screening programs",
      "Internal audits, VSDs, and BIS / DDTC engagement",
    ],
    image: IMG_EXPORT,
    layout: "image-left",
  },
  {
    n: "03",
    Icon: Landmark,
    title: "FAR / DFARS Compliance",
    sub: "Government contracting, done to standard.",
    body:
      "Federal contractors and their suppliers live under the Federal Acquisition Regulation and its defense supplement. We help primes and subcontractors build the clause flow-down, cybersecurity, and supply-chain controls those requirements demand — so a contract award does not later become an audit finding or a False Claims Act exposure.",
    bullets: [
      "FAR / DFARS clause analysis and flow-down management",
      "Prohibited-source (Section 889) and supply-chain screening",
      "Subcontractor compliance and mandatory-disclosure programs",
      "Contract close-out, records, and DCAA / DCMA audit readiness",
    ],
    image: IMG_DEFENSE,
    layout: "image-right",
  },
  {
    n: "04",
    Icon: Lock,
    title: "CMMC",
    sub: "Certification-ready, not just self-attested.",
    body:
      "Defense contractors handling federal contract information and controlled unclassified information must meet the Cybersecurity Maturity Model Certification. We take primes and subcontractors from gap assessment to certification readiness — implementing the NIST SP 800-171 controls behind DFARS 252.204-7012, closing findings, and assembling the evidence a C3PAO will expect at assessment.",
    bullets: [
      "CMMC Level 1 and Level 2 scoping and gap assessments",
      "NIST SP 800-171 control implementation and SSP authoring",
      "POA&M remediation and evidence / artifact preparation",
      "C3PAO assessment readiness and DFARS 252.204-7012 alignment",
    ],
    image: IMG_CMMC,
    layout: "image-left",
  },
  {
    n: "04",
    Icon: BrainCircuit,
    title: "AI Governance & Compliance",
    sub: "Govern the models before the regulators do.",
    body:
      "AI systems now carry their own regulatory weight — and their own liability. We help organizations stand up AI governance programs that inventory models, assess risk, and document compliance with the emerging body of law: the EU AI Act, the NIST AI Risk Management Framework, and ISO/IEC 42001. The goal is defensible adoption — using AI without inheriting an enforcement action or a discrimination claim.",
    bullets: [
      "AI system inventory, risk classification, and impact assessments",
      "Governance aligned to the EU AI Act, NIST AI RMF, and ISO/IEC 42001",
      "Model documentation, human-oversight, and transparency controls",
      "Third-party model due diligence and acceptable-use policy",
    ],
    image: IMG_AI,
    layout: "image-left",
  },
  {
    n: "05",
    Icon: Fingerprint,
    title: "Privacy Compliance",
    sub: "Personal data, handled lawfully.",
    body:
      "Personal data is regulated on every continent your business touches. We build privacy programs that map data flows, establish a lawful basis for processing, and stand up the notices, rights-handling, and safeguards that GDPR, CCPA/CPRA, and their growing list of successors demand — before a regulator or the plaintiff's bar asks to see them.",
    bullets: [
      "Data mapping, records of processing (RoPA), and DPIAs",
      "GDPR, CCPA / CPRA, and multi-state privacy-law compliance",
      "Privacy notices, consent, and data-subject rights workflows",
      "Vendor DPAs, cross-border transfer, and breach-response planning",
    ],
    image: IMG_PRIVACY,
    layout: "image-right",
  },
  {
    n: "03",
    Icon: HardHat,
    title: "Safety Consulting",
    sub: "OSHA-aligned safety management systems.",
    body:
      "From a single hazardous facility to a multinational manufacturing footprint, we design safety management systems your supervisors will actually use. Programs are written to OSHA, ANSI, and ISO 45001 expectations, and tuned for the realities of your operation.",
    bullets: [
      "Job hazard analyses (JHA) and PSM compliance",
      "Safety committee structure and KPI design",
      "Contractor safety qualification programs",
      "OSHA VPP Star preparation and audit defense",
    ],
    image: IMG_FOUNDER,
    layout: "image-right",
  },
  {
    n: "08",
    Icon: LifeBuoy,
    title: "Business Continuity",
    sub: "Ready for the day operations stop.",
    body:
      "Fires, floods, cyber events, and supply shocks do not wait for a plan. We build business-continuity and disaster-recovery programs that keep critical operations running — identifying essential functions, setting recovery objectives, and rehearsing the response until your people can execute it without the binder. Programs are aligned to ISO 22301 and NIST guidance and tested against realistic scenarios.",
    bullets: [
      "Business impact analysis and critical-function mapping",
      "Continuity of operations (COOP) and disaster-recovery plans",
      "Recovery objectives (RTO / RPO) and crisis-governance structure",
      "Tabletop exercises and ISO 22301-aligned program design",
    ],
    image: IMG_CONTINUITY,
    layout: "image-left",
  },
  {
    n: "04",
    Icon: FileCheck2,
    title: "Implementation & Training",
    sub: "We do not hand you a binder.",
    body:
      "A plan is worthless until the line worker, the export clerk, and the site manager all behave differently. Chesterton stays through implementation — embedding controls, training personnel by role, and measuring adoption against the metrics your auditors will eventually ask for.",
    bullets: [
      "Role-based training (LMS-ready modules and live workshops)",
      "Procedure rollouts with adoption metrics",
      "Embedded interim CSO / TCO / Safety Director services",
      "Quarterly program health reviews",
    ],
    image: IMG_FACILITY,
    layout: "image-left",
  },
  {
    n: "05",
    Icon: ShieldAlert,
    title: "Armed Guards",
    sub: "Vetted personnel. Standards beyond the statute.",
    body:
      "Chesterton fields licensed armed security personnel for client facilities, project sites, and high-value logistics. Our recruiting, vetting, training, and recurrent-qualification standards intentionally exceed every state-mandated minimum we operate under — because the legal minimum is rarely enough to stop a threat, and often enough to create one.",
    bullets: [
      "Licensed armed officers drawn from law-enforcement and military backgrounds",
      "Background, psychological, and firearms qualification beyond statutory minimums",
      "Recurrent training on live-fire, use-of-force, de-escalation, and medical",
      "Site-specific post orders integrated with your security plan and incident response",
    ],
    image: IMG_SECURITY,
    layout: "image-right",
  },
  {
    n: "06",
    Icon: Church,
    title: "Church Security",
    sub: "A calling, not a side practice.",
    body:
      "Chesterton is a Christian company, and we treat the protection of houses of worship as a calling. Because our core engagements are in high-risk industries, we can bring industrial-grade methodology to churches at pricing congregations can actually budget. The tone is softer at the threshold; the underlying threat modeling, armed-officer standards, and response planning are the same we deliver to operators where threats are ever-present.",
    bullets: [
      "Congregation-tailored security plans drawn from industrial risk methodology",
      "Vetted armed personnel deployed with discretion in worship settings",
      "Plain-clothes safety teams, usher protocols, and greeter training",
      "Active-threat response, lockdown drills, and youth-ministry safeguards",
    ],
    image: IMG_CHURCH,
    layout: "image-left",
  },
];

const process = [
  { step: "01", title: "Diagnose", text: "Confidential intake, document review, and on-site walk-down with your leadership." },
  { step: "02", title: "Design", text: "Plans, controls, and training scoped to your risk profile and regulator expectations." },
  { step: "03", title: "Implement", text: "Embedded with your teams to install procedures, run training, and validate adoption." },
  { step: "04", title: "Sustain", text: "Quarterly health checks, audit-readiness drills, and unlimited principal access." },
];

export default function Services() {
  return (
    <div data-testid="services-page" className="bg-slate-950">
      {/* HERO */}
      <section data-testid="services-hero" className="bg-slate-950 border-b border-slate-800 pt-20 pb-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8 anim-up">
              <p className="overline mb-6">Capabilities</p>
              <h1 className="font-serif text-5xl sm:text-6xl lg:text-8xl text-slate-50 leading-[0.95] tracking-tight">
                Eleven disciplines.<br />
                One <span className="italic text-amber-500">defensible</span> program.
              </h1>
            </div>
            <p className="lg:col-span-4 text-slate-400 text-lg leading-relaxed anim-up anim-delay-2">
              Chesterton engagements are sized to your operation — from a six-week
              export-classification sprint to multi-year, embedded implementations
              spanning facilities on three continents.
            </p>
          </div>
        </div>
      </section>

      {/* SERVICE SECTIONS */}
      {sections.map((s, idx) => {
        const Icon = s.Icon;
        const imageRight = idx % 2 === 0;
        return (
          <section
            key={s.title}
            data-testid={`service-section-${idx + 1}`}
            className={`py-24 lg:py-32 border-b border-slate-800 ${
              idx % 2 === 0 ? "bg-slate-950" : "bg-slate-900"
            }`}
          >
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
                <div className={`lg:col-span-6 ${imageRight ? "lg:order-1" : "lg:order-2"}`}>
                  <div className="flex items-center gap-4 mb-8">
                    <span className="font-serif text-slate-600 text-3xl">{String(idx + 1).padStart(2, "0")}</span>
                    <div className="h-px flex-1 bg-slate-800" />
                    <Icon className="w-7 h-7 text-amber-500" strokeWidth={1.2} />
                  </div>
                  <p className="overline mb-4">{s.sub}</p>
                  <h2 className="font-serif text-4xl sm:text-5xl text-slate-50 leading-[1.05] tracking-tight mb-6">
                    {s.title}
                  </h2>
                  <p className="text-slate-300 text-lg leading-relaxed mb-8">{s.body}</p>
                  <ul className="space-y-3">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3 text-slate-300">
                        <Check className="w-4 h-4 text-amber-500 mt-1.5 shrink-0" strokeWidth={2} />
                        <span className="leading-relaxed">{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={`lg:col-span-6 ${imageRight ? "lg:order-2" : "lg:order-1"}`}>
                  <div className="relative aspect-[4/5] overflow-hidden border border-slate-800">
                    <img
                      src={s.image}
                      alt={s.title}
                      className="w-full h-full object-cover grayscale-[35%] hover:grayscale-0 transition-all duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent" />
                    <div className="absolute bottom-6 left-6 right-6">
                      <p className="overline">{s.title}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* PROCESS */}
      <section data-testid="services-process" className="bg-slate-950 py-28 lg:py-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mb-16">
            <p className="overline mb-6">How We Work</p>
            <h2 className="font-serif text-4xl sm:text-5xl text-slate-50 leading-[1.05] tracking-tight max-w-3xl">
              A measured, four-stage <span className="italic text-amber-500">engagement</span>.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-800 border border-slate-800">
            {process.map((p) => (
              <div key={p.step} data-testid={`process-step-${p.step}`} className="bg-slate-950 p-10">
                <div className="font-serif text-amber-500 text-5xl mb-4">{p.step}</div>
                <h3 className="font-serif text-2xl text-slate-50 mb-3">{p.title}</h3>
                <p className="text-slate-400 leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-slate-900 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <h2 className="lg:col-span-8 font-serif text-4xl sm:text-5xl lg:text-6xl text-slate-50 leading-[1.05] tracking-tight">
              Tell us where it <span className="italic text-amber-500">hurts</span>.
              <br />We will design what comes next.
            </h2>
            <div className="lg:col-span-4 lg:text-right">
              <Link
                to="/contact"
                data-testid="services-cta-button"
                className="inline-flex items-center gap-3 bg-amber-600 hover:bg-amber-500 text-white px-8 py-5 text-xs uppercase tracking-[0.22em] font-medium transition-colors"
              >
                Start a Conversation
                <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
